import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FileText, Globe, Users, Clock, Shield, Award, CheckCircle, ArrowUpRight, Star } from 'lucide-react';
import aboutImg from '../../assets/5.png';

const About = () => {
  const { t } = useTranslation();

  const serviceCards = [
    { icon: <FileText size={22} color="#4792f4" />, bg: 'rgba(71,146,244,0.15)', label: t('aboutServiceItem1') },
    { icon: <Globe size={22} color="#4792f4" />,    bg: 'rgba(71,146,244,0.15)', label: t('aboutServiceItem2') },
    { icon: <Users size={22} color="#10b981" />,    bg: 'rgba(16,185,129,0.15)', label: t('aboutServiceItem3') },
  ];

  const highlights = [
    { icon: <Shield size={20} color="#4792f4" />,      label: t('aboutBestItem1') },
    { icon: <Clock size={20} color="#10b981" />,       label: t('aboutBestItem2') },
    { icon: <CheckCircle size={20} color="#f59e0b" />, label: t('aboutBestItem3') },
    { icon: <Award size={20} color="#a855f7" />,       label: t('aboutBestItem4') },
  ];

  const whyBullets = t('aboutWhyText')
    .split(/\.\s+/)
    .map(s => s.replace(/\.$/, '').trim())
    .filter(Boolean);

  return (
    <div className="ab-section" id="about">
      <style>{`
        .ab-section {
          background: linear-gradient(135deg, #0f2350 0%, #1b3f82 50%, #2563eb 100%);
          border-top: 1px solid rgba(71,146,244,0.30);
          padding: 5rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3rem;
          box-sizing: border-box;
          overflow: hidden;
          position: relative;
        }
        .ab-orb {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          z-index: 0;
          filter: blur(60px);
        }
        .ab-content { position: relative; z-index: 1; width: 100%; display: contents; }
        .ab-header { max-width: 1200px; width: 100%; position: relative; z-index: 1; }
        .ab-badge {
          display: inline-flex; align-items: center; gap: 0.5rem;
          background: rgba(71,146,244,0.18);
          border: 1px solid rgba(71,146,244,0.45);
          border-radius: 50px; padding: 0.3rem 1rem;
          font-size: 0.78rem; font-weight: 700; color: #a8c8ff;
          letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 0.75rem;
        }
        .ab-title {
          font-size: clamp(1.8rem, 3.5vw, 2.8rem); font-weight: 700;
          color: #ffffff; margin: 0 0 0.6rem; letter-spacing: -0.02em;
        }
        .ab-intro { font-size: 1rem; color: #c0d8ff; margin: 0; line-height: 1.7; max-width: 680px; }

        .ab-main {
          max-width: 1200px; width: 100%;
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 2rem; align-items: stretch; position: relative; z-index: 1;
        }
        .ab-img-wrap {
          border-radius: 18px; overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.35); min-height: 360px;
        }
        .ab-img-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .ab-cards-col { display: flex; flex-direction: column; gap: 0.85rem; }
        .ab-section-label {
          font-size: 0.78rem; font-weight: 700; color: #a8c8ff;
          letter-spacing: 0.1em; text-transform: uppercase; margin: 0 0 0.25rem;
          display: flex; align-items: center; gap: 0.5rem;
        }
        .ab-svc-card {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 12px; padding: 1rem 1.1rem;
          display: flex; align-items: flex-start; gap: 0.75rem;
          cursor: default;
        }
        .ab-svc-icon {
          width: 40px; height: 40px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .ab-svc-text { font-size: 0.85rem; color: #d0e4ff; line-height: 1.5; margin: 0; padding-top: 0.2rem; flex: 1; }
        .ab-arrow { color: rgba(255,255,255,0.25); flex-shrink: 0; margin-top: 2px; }

        .ab-divider-wrap { max-width: 1200px; width: 100%; position: relative; z-index: 1; }
        .ab-divider-line { height: 1px; background: rgba(71,146,244,0.25); transform-origin: left; }

        .ab-why {
          max-width: 1200px; width: 100%;
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 2rem; align-items: start; position: relative; z-index: 1;
        }
        .ab-bullets { display: flex; flex-direction: column; gap: 0.65rem; }
        .ab-bullet {
          display: flex; align-items: flex-start; gap: 0.65rem;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-left: 3px solid rgba(71,146,244,0.5);
          border-radius: 10px; padding: 0.7rem 1rem;
          font-size: 0.88rem; color: #d0e4ff; line-height: 1.55;
        }
        .ab-bullet svg { flex-shrink: 0; margin-top: 2px; }
        .ab-why-right { display: flex; flex-direction: column; gap: 1rem; }
        .ab-info-card {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 14px; padding: 1.25rem 1.4rem;
          display: flex; flex-direction: column; gap: 0.6rem;
        }
        .ab-info-card-label {
          font-size: 0.75rem; font-weight: 700; color: #a8c8ff;
          text-transform: uppercase; letter-spacing: 0.08em;
          display: flex; align-items: center; gap: 0.4rem;
        }
        .ab-info-card-text { font-size: 0.9rem; color: #d0e4ff; line-height: 1.7; margin: 0; }

        .ab-highlights {
          max-width: 1200px; width: 100%;
          display: grid; grid-template-columns: repeat(4,1fr);
          gap: 1rem; position: relative; z-index: 1;
        }
        .ab-hl-card {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 12px; padding: 1.25rem 1rem;
          display: flex; flex-direction: column; align-items: flex-start; gap: 0.6rem;
        }
        .ab-hl-icon {
          width: 38px; height: 38px; border-radius: 9px;
          background: rgba(255,255,255,0.08);
          display: flex; align-items: center; justify-content: center;
        }
        .ab-hl-label { font-size: 0.85rem; color: #d0e4ff; line-height: 1.5; margin: 0; font-weight: 500; }

        .ab-cta-row {
          max-width: 1200px; width: 100%;
          background: linear-gradient(135deg, #4792f4 0%, #2563eb 100%);
          border-radius: 16px; padding: 1.75rem 2rem;
          display: flex; align-items: center; justify-content: space-between;
          gap: 1.5rem; flex-wrap: wrap;
          box-shadow: 0 8px 24px rgba(71,146,244,0.3);
          position: relative; z-index: 1;
        }
        .ab-cta-text { font-size: 1rem; color: #fff; margin: 0; font-weight: 500; line-height: 1.6; }
        .ab-cta-link {
          display: inline-flex; align-items: center; gap: 0.4rem;
          background: rgba(255,255,255,0.18); border: 1px solid rgba(255,255,255,0.35);
          border-radius: 8px; padding: 0.55rem 1.25rem;
          color: #fff; font-weight: 700; font-size: 0.95rem;
          text-decoration: none; white-space: nowrap; transition: background 0.2s;
        }
        .ab-cta-link:hover { background: rgba(255,255,255,0.28); }

        @media (max-width: 968px) {
          .ab-main, .ab-why { grid-template-columns: 1fr; }
          .ab-img-wrap { min-height: 240px; }
          .ab-highlights { grid-template-columns: repeat(2,1fr); }
        }
        @media (max-width: 560px) {
          .ab-section { padding: 3rem 1rem; gap: 2rem; }
          .ab-highlights { grid-template-columns: 1fr 1fr; }
          .ab-cta-row { flex-direction: column; align-items: flex-start; }
        }
      `}</style>

      {/* Floating background orbs */}
      <motion.div className="ab-orb" style={{ width: 400, height: 400, background: 'rgba(71,146,244,0.12)', top: -100, right: -100 }}
        animate={{ y: [0, 30, 0], x: [0, -20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div className="ab-orb" style={{ width: 300, height: 300, background: 'rgba(16,185,129,0.08)', bottom: 200, left: -80 }}
        animate={{ y: [0, -25, 0], x: [0, 15, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      />
      <motion.div className="ab-orb" style={{ width: 200, height: 200, background: 'rgba(168,85,247,0.07)', top: '40%', left: '45%' }}
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      />

      {/* Header */}
      <motion.div
        className="ab-header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <motion.div
          className="ab-badge"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          GeoGlobalGate LTD
        </motion.div>
        <h2 className="ab-title">{t('aboutTitle')}</h2>
        <p className="ab-intro">{t('aboutText')}</p>
      </motion.div>

      {/* Image + service cards */}
      <div className="ab-main">
        {/* Image with float */}
        <motion.div
          className="ab-img-wrap"
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          animate={{ y: [0, -8, 0] }}
          style={{ animationFillMode: 'both' }}
        >
          <motion.img
            src={aboutImg} alt="GeoGlobalGate"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>

        {/* Service cards stagger */}
        <motion.div
          className="ab-cards-col"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.15 } } }}
        >
          <motion.p
            className="ab-section-label"
            variants={{ hidden: { opacity: 0, x: 20 }, visible: { opacity: 1, x: 0, transition: { duration: 0.4 } } }}
          >
            <FileText size={13} />{t('aboutServicesTitle')}
          </motion.p>
          {serviceCards.map((card, i) => (
            <motion.div
              className="ab-svc-card"
              key={i}
              variants={{ hidden: { opacity: 0, x: 30 }, visible: { opacity: 1, x: 0, transition: { duration: 0.5 } } }}
              whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.11)', borderColor: 'rgba(71,146,244,0.45)', transition: { duration: 0.2 } }}
            >
              <motion.div
                className="ab-svc-icon"
                style={{ background: card.bg }}
                whileHover={{ rotate: [0, -10, 10, 0], transition: { duration: 0.4 } }}
              >
                {card.icon}
              </motion.div>
              <p className="ab-svc-text">{card.label}</p>
              <motion.div whileHover={{ x: 3, y: -3, transition: { duration: 0.2 } }}>
                <ArrowUpRight size={14} className="ab-arrow" />
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Animated divider */}
      <div className="ab-divider-wrap">
        <motion.div
          className="ab-divider-line"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: 'easeInOut' }}
        />
      </div>

      {/* Why bullets + info cards */}
      <div className="ab-why">
        <motion.div
          className="ab-why-left"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.6 }}
        >
          <p className="ab-section-label"><Star size={13} />{t('aboutWhyTitle')}</p>
          <div className="ab-bullets">
            {whyBullets.map((sentence, i) => (
              <motion.div
                className="ab-bullet"
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                whileHover={{ borderLeftColor: 'rgba(71,146,244,0.9)', backgroundColor: 'rgba(255,255,255,0.09)', transition: { duration: 0.2 } }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.1 + 0.2 }}
                >
                  <CheckCircle size={15} color="#4792f4" />
                </motion.div>
                {sentence}
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="ab-why-right"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          {[
            { label: 'GeoGlobalGate LTD', icon: <Shield size={13} />, text: t('aboutRepresentativeText') },
            { label: t('aboutBestChoiceTitle'), icon: <CheckCircle size={13} />, text: t('aboutGuarantee') },
          ].map((card, i) => (
            <motion.div
              className="ab-info-card"
              key={i}
              whileHover={{ scale: 1.02, borderColor: 'rgba(71,146,244,0.4)', transition: { duration: 0.2 } }}
            >
              <div className="ab-info-card-label">{card.icon} {card.label}</div>
              <p className="ab-info-card-text">{card.text}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Animated divider */}
      <div className="ab-divider-wrap">
        <motion.div
          className="ab-divider-line"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: 'easeInOut' }}
        />
      </div>

      {/* Highlight cards */}
      <motion.div
        className="ab-highlights"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}
      >
        {highlights.map((h, i) => (
          <motion.div
            className="ab-hl-card"
            key={i}
            variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
            whileHover={{ scale: 1.04, borderColor: 'rgba(71,146,244,0.45)', backgroundColor: 'rgba(255,255,255,0.1)', transition: { duration: 0.2 } }}
          >
            <motion.div
              className="ab-hl-icon"
              whileInView={{ rotate: [0, 360] }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
            >
              {h.icon}
            </motion.div>
            <p className="ab-hl-label">{h.label}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* CTA */}
      <motion.div
        className="ab-cta-row"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
      >
        <p className="ab-cta-text">{t('aboutCtaText')}</p>
        <motion.a
          className="ab-cta-link"
          href="mailto:t1@geoglobalgate.com"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
        >
          t1@geoglobalgate.com <ArrowUpRight size={16} />
        </motion.a>
      </motion.div>
    </div>
  );
};

export default About;
